/**
 * Web Vitals Monitoring Utility
 * Tracks Core Web Vitals and other performance metrics
 */

// Metric types
export interface WebVitalsMetric {
  name: 'LCP' | 'FID' | 'CLS' | 'FCP' | 'TTFB' | 'INP';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
  id: string;
  navigationType?: string;
}

// Thresholds based on Google's Core Web Vitals
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint (ms)
  FID: { good: 100, poor: 300 },   // First Input Delay (ms)
  CLS: { good: 0.1, poor: 0.25 },  // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint (ms)
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte (ms)
  INP: { good: 200, poor: 500 },   // Interaction to Next Paint (ms)
};

/**
 * Get rating based on metric value
 */
function getRating(name: WebVitalsMetric['name'], value: number): WebVitalsMetric['rating'] {
  const threshold = THRESHOLDS[name];
  if (!threshold) return 'good';

  if (name === 'CLS') {
    // CLS is better when lower
    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  }

  // Other metrics are better when lower
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Generate unique ID for metric
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Update the in-window web-vitals summary consumed by ErrorBoundary
 */
function reportMetric(metric: WebVitalsMetric) {
  // Store in global for error reporting
  if (typeof window !== 'undefined') {
    (window as any).__WEB_VITALS__ = {
      ...((window as any).__WEB_VITALS__ || {}),
      [metric.name]: metric.value,
    };
  }

  // Log in development
  if (import.meta.env.MODE === 'development') {
    console.log(`[Web Vitals] ${metric.name}:`, metric.value, `(${metric.rating})`);
  }
}

/**
 * Observe Largest Contentful Paint (LCP)
 */
function observeLCP() {
  if (!('PerformanceObserver' in window)) return;

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };

    if (lastEntry) {
      const metric: WebVitalsMetric = {
        name: 'LCP',
        value: Math.round(lastEntry.startTime),
        rating: getRating('LCP', lastEntry.startTime),
        id: generateId(),
      };
      reportMetric(metric);
    }
  });

  observer.observe({ entryTypes: ['largest-contentful-paint'] as any });
}

/**
 * Observe First Input Delay (FID)
 */
function observeFID() {
  if (!('PerformanceObserver' in window)) return;

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach((entry) => {
      const eventEntry = entry as PerformanceEntry & { processingStart: number; startTime: number };
      const delay = eventEntry.processingStart - eventEntry.startTime;

      const metric: WebVitalsMetric = {
        name: 'FID',
        value: Math.round(delay),
        rating: getRating('FID', delay),
        id: generateId(),
      };
      reportMetric(metric);
    });
  });

  observer.observe({ entryTypes: ['first-input'] as any });
}

/**
 * Observe Cumulative Layout Shift (CLS)
 */
function observeCLS() {
  if (!('PerformanceObserver' in window)) return;

  let clsValue = 0;
  let sessionEntries: any[] = [];

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries() as any[];

    entries.forEach((entry) => {
      // Only count if not recently input
      if (!entry.hadRecentInput) {
        clsValue += entry.value;
        sessionEntries.push(entry);
      }
    });

    // Report CLS on page hide
    const reportCLS = () => {
      const metric: WebVitalsMetric = {
        name: 'CLS',
        value: Math.round(clsValue * 1000) / 1000,
        rating: getRating('CLS', clsValue),
        id: generateId(),
      };
      reportMetric(metric);
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        reportCLS();
      }
    });
  });

  observer.observe({ entryTypes: ['layout-shift'] as any });
}

/**
 * Observe First Contentful Paint (FCP)
 */
function observeFCP() {
  if (!('PerformanceObserver' in window)) return;

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach((entry) => {
      const paintEntry = entry as PerformanceEntry & { startTime: number };
      if (entry.name === 'first-contentful-paint') {
        const metric: WebVitalsMetric = {
          name: 'FCP',
          value: Math.round(paintEntry.startTime),
          rating: getRating('FCP', paintEntry.startTime),
          id: generateId(),
        };
        reportMetric(metric);
      }
    });
  });

  observer.observe({ entryTypes: ['paint'] as any });
}

/**
 * Observe Time to First Byte (TTFB)
 */
function observeTTFB() {
  if (!('performance' in window)) return;

  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (navigation) {
    const ttfb = navigation.responseStart - navigation.startTime;
    const metric: WebVitalsMetric = {
      name: 'TTFB',
      value: Math.round(ttfb),
      rating: getRating('TTFB', ttfb),
      id: generateId(),
    };
    reportMetric(metric);
  }
}

/**
 * Initialize Web Vitals monitoring
 */
export function initWebVitals() {
  if (typeof window === 'undefined') return;

  // Use requestIdleCallback or setTimeout to not block main thread
  const schedule = (window as any).requestIdleCallback || setTimeout;

  schedule(() => {
    observeLCP();
    observeFID();
    observeCLS();
    observeFCP();
    observeTTFB();
  });
}

/**
 * Get current Web Vitals summary
 */
export function getWebVitalsSummary(): Partial<Record<WebVitalsMetric['name'], number>> {
  if (typeof window === 'undefined') return {};
  return (window as any).__WEB_VITALS__ || {};
}

/**
 * Check if all Core Web Vitals are good
 */
export function areCoreWebVitalsGood(): boolean {
  const vitals = getWebVitalsSummary();
  return (
    (vitals.LCP || Infinity) <= THRESHOLDS.LCP.good &&
    (vitals.FID || Infinity) <= THRESHOLDS.FID.good &&
    (vitals.CLS || Infinity) <= THRESHOLDS.CLS.good
  );
}

export default {
  init: initWebVitals,
  getSummary: getWebVitalsSummary,
  areCoreWebVitalsGood,
};
